import { useReport, useReportMonitors } from '@ltvco/refresh-lib/hooks';
import { Box, Grid, useTheme } from '@ltvco/refresh-lib/theme';
import {
  useScreenSize,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  enqueueSnackbar,
  ReportLoading,
  ReportNullState,
  useRerunVehicleReport,
} from '@ltvco/refresh-lib/v1';
import {
  FinanceCheckSection,
  KeeperHistorySection,
  MOTSection,
  ReportActionsWithDateUpdated,
  ReportChangesOverview,
  ReportGeneratingScreen,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  SafetyRecallsSection,
  StolenCheckSection,
  UKVehicleReportDisclaimer,
  UKVehicleReportOverview,
  WriteOffSalvageSection,
} from '@ltvco/refresh-lib/v2';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { getUkNavItems } from './ukNavItems';
import {
  getUkVehicleHeaderTitle,
  getUkVehicleSubheaderTitle,
  isUKVehicleReportData,
  VEHICLE_MONITOR_REPORT_TYPE,
} from './utils';

import type { ReportRouteProps } from '../types';

export const UKVehicleReport = ({
  permalink,
  firstReportScreen,
}: ReportRouteProps) => {
  const queryResult = useReport(permalink);
  const { reportIsMonitored, toggleReportIsMonitored } =
    useReportMonitors(permalink);
  const rerunReport = useRerunVehicleReport();
  const { hash } = useLocation();
  const { isMobile } = useScreenSize();
  const theme = useTheme();

  useScrollToSectionOnNavigate(hash, queryResult);

  const ukReport = isUKVehicleReportData(queryResult.data)
    ? queryResult.data
    : undefined;
  const automobile = ukReport?.entities?.vehicles?.automobiles?.[0];
  const updatedAt = ukReport?.meta?.updated_at ?? '';

  const headerTitle = automobile
    ? getUkVehicleHeaderTitle(automobile)
    : 'UK Vehicle';
  const subheaderTitle = automobile
    ? getUkVehicleSubheaderTitle(automobile)
    : '';

  const navTitleStyle = useMemo(
    () => ({
      whiteSpace: 'normal' as const,
      wordWrap: 'break-word' as const,
      color: theme.palette.text.primary,
    }),
    [theme.palette.text.primary]
  );

  const ukNavItems = useMemo(
    () => (automobile ? getUkNavItems(automobile) : []),
    [automobile]
  );

  useEffect(() => {
    document.title = `${headerTitle} - BeenVerified`;
  }, [headerTitle]);

  const handleMonitorClick = useCallback(() => {
    toggleReportIsMonitored({
      queryParams: {
        'report_monitor[is_claimed]': 'false',
        'report_monitor[report_type]': VEHICLE_MONITOR_REPORT_TYPE,
      },
      successCallback: () => {
        enqueueSnackbar(
          `Monitor was ${reportIsMonitored ? 'disabled' : 'enabled'}`,
          { variant: 'success' }
        );
      },
    });
  }, [reportIsMonitored, toggleReportIsMonitored]);

  const handleRerunClick = useCallback(() => {
    const meta = queryResult.data?.meta;
    if (!meta) {
      return;
    }

    enqueueSnackbar('Currently rerunning the report!', {
      variant: 'success',
    });
    void rerunReport(meta);
  }, [queryResult.data?.meta, rerunReport]);

  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="vehicle"
        ready={!queryResult.isLoading}
        onComplete={firstReportScreen.onComplete}
      />
    );
  }

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={6} />;
  }

  if (!ukReport || !automobile) {
    return <ReportNullState />;
  }

  return (
    <Grid container direction="row" columns={12} spacing={7}>
      <Grid
        size={{ sm: 12, md: 4, lg: 4 }}
        sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' } }}
      >
        <ReportNavigationMenu
          reportType="Vehicle Report"
          headerTitle={headerTitle}
          subheaderTitle={subheaderTitle}
          menuItems={ukNavItems}
          customTitleStyle={navTitleStyle}
        />
      </Grid>
      <Grid size={{ sm: 12, md: 8, lg: 8 }} marginTop={0.5}>
        {reportIsMonitored && (
          <ReportChangesOverview
            permalink={permalink}
            navLinkData={ukNavItems}
          />
        )}
        <Box
          height={30}
          bgcolor="background.default"
          position="sticky"
          marginBottom="-10px"
          marginX={-1}
          top={52}
          zIndex={10}
          display={{ xs: 'none', sm: 'block', md: 'block', lg: 'block' }}
        />
        <ReportActionsWithDateUpdated
          reportTitle="UK Vehicle"
          reportType="vehicle"
          dateUpdated={updatedAt}
          reportUpgraded={false}
          reportDataForListV2={undefined}
          hidePdf
          hidePreferences
        />
        <UKVehicleReportOverview
          reportData={ukReport}
          updatedDate={updatedAt}
          isMonitored={reportIsMonitored}
          onMonitorClick={handleMonitorClick}
          onRerunClick={handleRerunClick}
        />
        <KeeperHistorySection automobile={automobile} />
        <MOTSection automobile={automobile} />
        <WriteOffSalvageSection automobile={automobile} updatedAt={updatedAt} />
        <FinanceCheckSection automobile={automobile} updatedAt={updatedAt} />
        <StolenCheckSection thefts={automobile.thefts} updatedAt={updatedAt} />
        <SafetyRecallsSection automobile={automobile} updatedAt={updatedAt} />
        <UKVehicleReportDisclaimer />
      </Grid>
      {isMobile && (
        <ReportNavigationMobileMenu
          reportType="Vehicle Report"
          headerTitle={headerTitle}
          subheaderTitle={subheaderTitle}
          menuItems={ukNavItems}
          customTitleStyle={navTitleStyle}
          showSectionTitleOnOverview
        />
      )}
    </Grid>
  );
};
